/* eslint-disable react-hooks/rules-of-hooks */
import { QueryClient, useQuery } from '@tanstack/react-query';
import request from '~/bin/httpRequest';
import { Encryption } from '~/helpers/encryption';
import { CUSTOM_INVENTORY, MERCHANT } from '~/store/States/global';

export const req = (options, sessionIdDisable) => request.genericHandler(options, sessionIdDisable).then((res) => {
  if (res.error) {
    return { ok: false, body: res.error.data, headers: res.error.headers };
  }
  return { ok: true, body: res.data.data, headers: res.data.headers };
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 300000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    },
  },
});

/**
 * API o hook para realizar una consulta.
 * @param type {'hook' | 'async'} - Indica si se usa como hook o como función asíncrona.
 * @param params {Array} - Parámetros de la consulta, como el nombre de la consulta y sus argumentos.
 * @param options {Object} - Opciones de configuración para la consulta, como método HTTP, URL, etc.
 * @param enabled {boolean} - Indica si la consulta está habilitada o no. Por defecto es true.
 * @returns {Promise<{ok: boolean, body: *} | {ok: boolean, body: *}>|UseQueryResult<{ok: boolean, body: *} | {ok: boolean, body: *}, DefaultError>}
 */
export const fetchQuery = (type, params, options, enabled = true) => {
   const fn = () => req(options, null);
  if (type === 'hook') {
    return useQuery({ queryKey: params, queryFn: fn, enabled });
  }
  return queryClient.fetchQuery({ queryKey: params, queryFn: fn });
};

export const useData = (useFn) => {
  const { data } = useFn();
  if (!data?.ok) {
    return null;
  }
  return data.body;
};

export const getData = async (getFn) => {
  const res = await getFn();
  if (res.ok) {
    return res.body;
  }
  return null;
};

const configQuery = config => () => req({
  method: 'GET',
  url: `${process.env.NEXT_PUBLIC_MONGO_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/${config}`,
}, null);

export const useConfig = (config, enabled = true) => useQuery({ queryKey: [config], queryFn: configQuery(config), enabled });

export const getConfig = config => queryClient.fetchQuery({ queryKey: [config], queryFn: configQuery(config) });

const espotQuery = espot => () => req({
  method: 'GET',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/${espot}`,
}, null);

export const useEspot = (espot, enabled = true) => useQuery({ queryKey: [espot], queryFn: espotQuery(espot), enabled });

// export const getEspot = espot => queryClient.fetchQuery({ queryKey: [espot], queryFn: espotQuery(espot) });

export const notifyAvailability = data => req({
  method: 'POST',
  url: `${process.env.NEXT_PUBLIC_MONGO_API}${process.env.NEXT_PUBLIC_MERCHANT}/notify/availability`,
  data,
}, null);

const encryption = new Encryption();

const configMCQuery = async () => {
  const res = await req({
    method: 'GET',
    url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/econfig`,
    loading: false,
  }, null);
  if (res.ok) {
    const isText = res.headers['content-type']?.startsWith('text/plain');
    const body = isText ? await encryption.decrypt(res.body) : res.body;
    return { ok: true, body };
  }
  return res;
}

const renewalsMCQuery = () => req({
  method: 'GET',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/renovaciones-automaticas`,
}, null);

const eSIMConfigQuery = () => req({
  method: 'GET',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/esim`,
}, null);

const TermsQuery = () => req({
  method: 'GET',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/terms`,
  responseType: 'text',
}, null);

export const useRenewalsConfig = () => useQuery({ queryKey: ['renewalsConfig'], queryFn: renewalsMCQuery });
export const getRenewalsConfig = () => queryClient.fetchQuery({ queryKey: ['renewalsConfig'], queryFn: renewalsMCQuery });

export const useConfigMC = (initialData) => useQuery({ queryKey: ['configMC'], queryFn: configMCQuery, initialData });

export const useTermsMC = () => useQuery({ queryKey: ['termsMC'], queryFn: TermsQuery });

export const getConfigMC = () => queryClient.fetchQuery({ queryKey: ['configMC'], queryFn: configMCQuery });

export const getEsimConfig = () => queryClient.fetchQuery({ queryKey: ['configESim'], queryFn: eSIMConfigQuery });

const itemsBySKU = (type, query, enabled = true) => {
  const params = ['itemsBySKU', query.sku.sort().join(',')];
  const fn = () => req({
    method: 'POST',
    url: `${process.env.NEXT_PUBLIC_MONGO_API}${process.env.NEXT_PUBLIC_MERCHANT}/products/items`,
    data: query,
  }, null);
  if (type === 'hook') {
    return useQuery({ queryKey: params, queryFn: fn, enabled });
  }
  return queryClient.fetchQuery({ queryKey: params, queryFn: fn });
};

// export const useItemsBySKU = (query, enabled = true) => itemsBySKU('hook', query, enabled);

export const getItemsBySKU = query => itemsBySKU('async', query);

const addons = (type, enabled = true) => {
  const params = ['addons'];
  const fn = () => req({
    method: 'GET',
    url: `${process.env.NEXT_PUBLIC_WCAAS}${process.env.NEXT_PUBLIC_MERCHANT}/addon`,
    loading: false,
  }, null);
  if (type === 'hook') {
    return useQuery({ queryKey: params, queryFn: fn, enabled });
  }
  return queryClient.fetchQuery({ queryKey: params, queryFn: fn });
};

// export const useAddons = (enabled = true) => addons('hook', enabled);

export const apiAddons = () => addons('async');

const categories = (type, enabled = true) => {
  const params = ['categories'];
  const fn = () => req({
    method: 'GET',
    url: `${process.env.NEXT_PUBLIC_MONGO_API}${process.env.NEXT_PUBLIC_MERCHANT}/categories`,
  }, null);
  if (type === 'hook') {
    return useQuery({ queryKey: params, queryFn: fn, enabled });
  }
  return queryClient.fetchQuery({ queryKey: params, queryFn: fn });
};

// export const useCategories = (enabled = true) => categories('hook', enabled);

export const apiCategories = () => categories('async');

const configHome = (type, enabled = true) => {
  const params = ['configHome'];
  const fn = () => req({
    method: 'GET',
    url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/home`,
    loading: false,
  }, null);
  if (type === 'hook') {
    return useQuery({ queryKey: params, queryFn: fn, enabled });
  }
  return queryClient.fetchQuery({ queryKey: params, queryFn: fn });
};

// export const useConfigHome = (enabled = true) => configHome('hook', enabled);

export const getConfigHome = () => configHome('async');

export const getProductItem = partNumber => queryClient.fetchQuery({ queryKey: ['productItem', partNumber], queryFn: () => req({
  method: 'GET',
  url: `${process.env.NEXT_PUBLIC_MONGO_API}${process.env.NEXT_PUBLIC_MERCHANT}/product/item/${partNumber}`,
}, null) });

export const getImagePassport = () => req({
  method: 'GET',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/config/tarjetas-internacionales`,
}, null);

export const uploadImagePassport = data => req({
  method: 'POST',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/upload/imagePassaporte`,
  data,
});

export const validateDocumentImage = (data, tipo) => req({
  method: 'POST',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/cart/validate-image/${tipo}`,
  data,
});

export const proofOfLife = (data) => req({
  method: 'POST',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/cart/proof-of-life`,
  data,
});

export const proofOfLifeValidate = (data) => req({
  method: 'POST',
  url: `${process.env.NEXT_PUBLIC_API}${process.env.NEXT_PUBLIC_MERCHANT}/cart/proof-of-life-validate`,
  data,
});

export const queryFunction = async (action, fetchQuery, nestedData = true) => {
  const res = await fetchQuery();
  const response = { action, success: false };
  if (res.ok) {
    response.data = nestedData ? { data: res.body } : res.body;
    response.success = true;
  } else {
    response.error = { data: res.body };
  }
  return response;
};

/**
 * Check inventories status
 * @param query {{ sku: Array<string> }}
 * @returns {Promise<{ok: boolean, body: any}>}
 */
export const inventoriesStatus = query => req({
  method: 'POST',
  url: `${CUSTOM_INVENTORY}${MERCHANT}/status`,
  data: query,
});
